// Propósito: ventana principal de chat con entrada, mensajes y adjuntos.
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { MessageBubble } from './MessageBubble';
import { useDebouncedCallback } from '@/features/chatbot/hooks/useDebounce';
import type {
  ChatbotMessage,
  ChatbotBotPublic,
  ChatbotInputConfig,
  ChatbotConversation,
  ChatbotContentType,
  FlowiseUpload,
  ChatbotLimitState,
} from '../../types/chatbot.types';
import { parseConciergeMenuCards } from '../../utils/parseConciergeMenuCards';

type AttachmentContentType = 'audio' | 'image' | 'file';

interface UploadResult {
  upload: FlowiseUpload;
  fileUrl: string;
  fileMetadata: { name: string; size: number; mimeType: string };
}

interface ChatWindowProps {
  bot: ChatbotBotPublic;
  messages: ChatbotMessage[];
  onSendMessage: (payload: {
    content: string;
    contentType: ChatbotContentType;
    uploads?: FlowiseUpload[];
    fileUrl?: string;
    fileMetadata?: { name: string; size: number; mimeType: string; type?: string };
  }) => Promise<void>;
  onNewConversation?: () => void;
  onBack: () => void;
  isSending: boolean;
  isLoadingMessages: boolean;
  isLoadingMoreMessages: boolean;
  hasMoreMessages: boolean;
  onLoadMoreMessages: () => void;
  conversationMode: 'SINGLE' | 'MULTIPLE';
  inputConfig?: ChatbotInputConfig;
  onUploadFile: (payload: {
    file: File;
    contentType: AttachmentContentType;
  }) => Promise<UploadResult>;
  conversations: ChatbotConversation[];
  onSelectConversation: (id: string) => void;
  onHideConversation?: (id: string) => void;
  currentConversationId: string | null;
  limitState?: ChatbotLimitState;
  onClearLimit?: () => void;
  /** Mensaje a repoblar en el input cuando hay error del bot */
  failedMessageContent?: string;
  /** Callback cuando se limpia el mensaje fallido */
  onFailedMessageCleared?: () => void;
  stickyLabel?: string | null;
  hideBack?: boolean;
}

function getAttachmentMaxSizeMb(
  contentType: AttachmentContentType,
  inputConfig?: ChatbotInputConfig
): number | undefined {
  if (contentType === 'image') return inputConfig?.images?.maxSizeMb;
  if (contentType === 'file') return inputConfig?.files?.maxSizeMb;
  return undefined;
}

function getAttachmentAvailabilityError(
  contentType: AttachmentContentType,
  inputConfig?: ChatbotInputConfig
): string | null {
  if (contentType === 'audio' && !inputConfig?.audio?.enabled) {
    return 'Los audios no están habilitados';
  }
  if (contentType === 'image' && inputConfig?.images?.enabled === false) {
    return 'Las imágenes no están habilitadas';
  }
  if (contentType === 'file' && !inputConfig?.files?.enabled) {
    return 'Los archivos no están habilitados';
  }
  return null;
}

function getAttachmentSizeError(
  file: File,
  contentType: AttachmentContentType,
  inputConfig?: ChatbotInputConfig
): string | null {
  const maxSizeMb = getAttachmentMaxSizeMb(contentType, inputConfig);
  if (!maxSizeMb) return null;
  const maxBytes = maxSizeMb * 1024 * 1024;
  if (file.size > maxBytes) {
    return `El archivo excede el límite de ${maxSizeMb}MB`;
  }
  return null;
}

function getAttachmentExtensionError(
  file: File,
  contentType: AttachmentContentType,
  inputConfig?: ChatbotInputConfig
): string | null {
  if (contentType !== 'file' || !inputConfig?.files?.allowedExtensions?.length) {
    return null;
  }

  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const allowed = inputConfig.files.allowedExtensions.map(value =>
    value.replace(/^\./, '').toLowerCase()
  );

  if (!allowed.includes(ext)) {
    return `Extensión no permitida. Permitidas: ${inputConfig.files.allowedExtensions.join(', ')}`;
  }
  return null;
}

function getAttachmentMimeError(file: File, contentType: AttachmentContentType): string | null {
  if (contentType === 'image' && !file.type.startsWith('image/')) {
    return 'El archivo seleccionado no es una imagen válida';
  }
  if (contentType === 'audio' && !file.type.startsWith('audio/')) {
    return 'El archivo seleccionado no es un audio válido';
  }
  return null;
}

const AUDIO_EXTENSION_TO_MIME: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  webm: 'audio/webm',
  m4a: 'audio/mp4',
};

const AUDIO_FILE_EXTENSIONS = Object.keys(AUDIO_EXTENSION_TO_MIME);
const DEFAULT_FILE_ACCEPT_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.xls', '.txt', '.csv'];

function getFileExtension(file: File): string {
  return file.name.split('.').pop()?.toLowerCase() || '';
}

function isAudioFile(file: File): boolean {
  const extension = getFileExtension(file);
  return file.type.startsWith('audio/') || AUDIO_FILE_EXTENSIONS.includes(extension);
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

function normalizeAudioFileMimeByExtension(file: File): File {
  const extension = getFileExtension(file);
  const expectedMime = AUDIO_EXTENSION_TO_MIME[extension];

  if (!expectedMime || file.type === expectedMime) {
    return file;
  }

  return new File([file], file.name, {
    type: expectedMime,
    lastModified: file.lastModified,
  });
}

function useLimitCountdown(resetAt: string | undefined, onClearLimit?: () => void): number {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!resetAt) {
      setTimeLeft(0);
      return;
    }

    const interval = window.setInterval(() => {
      const diff = Math.max(0, Math.ceil((new Date(resetAt).getTime() - Date.now()) / 1000));
      setTimeLeft(diff);
      if (diff === 0) {
        onClearLimit?.();
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [resetAt, onClearLimit]);

  return timeLeft;
}

// Duración mínima de un mensaje de audio en milisegundos. Evita el envío
// accidental al tap-and-release sobre el botón de grabación, que genera blobs
// inservibles de pocos chunks.
const MIN_AUDIO_DURATION_MS = 1000;

function useAudioRecording({
  onRecordingComplete,
  audioEnabled,
  maxDurationSeconds,
  setFileError,
}: {
  onRecordingComplete: (file: File) => void | Promise<void>;
  audioEnabled?: boolean;
  maxDurationSeconds?: number;
  setFileError: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number>(0);
  const discardRecordingRef = useRef(false);
  const shouldStopAfterStartRef = useRef(false);

  const stopRecording = useCallback((discard = false) => {
    discardRecordingRef.current = discard;
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    } else {
      shouldStopAfterStartRef.current = true;
    }
    setIsRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    stopRecording(true);
  }, [stopRecording]);

  const startRecording = useCallback(async () => {
    if (isRecording || !audioEnabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      discardRecordingRef.current = false;
      shouldStopAfterStartRef.current = false;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        if (recordingStreamRef.current) {
          recordingStreamRef.current.getTracks().forEach(track => track.stop());
          recordingStreamRef.current = null;
        }

        const elapsedMs = recordingStartedAtRef.current
          ? Date.now() - recordingStartedAtRef.current
          : 0;
        recordingStartedAtRef.current = 0;

        if (discardRecordingRef.current || recordingChunksRef.current.length === 0) {
          recordingChunksRef.current = [];
          return;
        }

        if (elapsedMs < MIN_AUDIO_DURATION_MS) {
          recordingChunksRef.current = [];
          setFileError('La grabación es demasiado corta (mínimo 1 segundo).');
          return;
        }

        const blob = new Blob(recordingChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        recordingChunksRef.current = [];
        const file = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
        void onRecordingComplete(file);
      };

      // timeslice de 1s: vacía chunks periódicamente para no perder audio
      // ya capturado si la grabación se interrumpe de forma abrupta.
      recorder.start(1000);
      recordingStartedAtRef.current = Date.now();
      setIsRecording(true);
      setRecordingSeconds(0);
      if (shouldStopAfterStartRef.current) {
        stopRecording();
        return;
      }
      const maxDuration = maxDurationSeconds ?? 120;

      recordingTimerRef.current = window.setInterval(() => {
        setRecordingSeconds(prev => {
          const next = prev + 1;
          if (next >= maxDuration) {
            stopRecording();
          }
          return next;
        });
      }, 1000);
    } catch (_error) {
      setFileError('No se pudo acceder al micrófono');
    }
  }, [
    audioEnabled,
    isRecording,
    maxDurationSeconds,
    onRecordingComplete,
    setFileError,
    stopRecording,
  ]);

  useEffect(() => {
    return () => {
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return {
    isRecording,
    recordingSeconds,
    cancelRecording,
    startRecording,
    stopRecording,
  };
}

function validateAttachmentAgainstConfig(
  file: File,
  contentType: AttachmentContentType,
  inputConfig?: ChatbotInputConfig
): string | null {
  return (
    getAttachmentAvailabilityError(contentType, inputConfig) ||
    getAttachmentSizeError(file, contentType, inputConfig) ||
    getAttachmentExtensionError(file, contentType, inputConfig) ||
    getAttachmentMimeError(file, contentType)
  );
}

function resolveOutgoingContentType(fileType: AttachmentContentType | null): ChatbotContentType {
  if (fileType === 'image') return 'IMAGE';
  if (fileType === 'audio') return 'AUDIO';
  if (fileType === 'file') return 'FILE';
  return 'TEXT';
}

function getAttachmentMessageContent(
  typedContent: string,
  selectedFile: File | null,
  selectedFileType: AttachmentContentType | null
): string {
  if (typedContent) return typedContent;
  if (!selectedFile || selectedFileType === 'audio') return '';
  return selectedFile.name;
}

async function uploadSelectedAttachment(
  selectedFile: File,
  selectedFileType: AttachmentContentType,
  onUploadFile: (payload: {
    file: File;
    contentType: AttachmentContentType;
  }) => Promise<UploadResult>
): Promise<{
  uploads: FlowiseUpload[];
  fileUrl: string;
  fileMetadata: { name: string; size: number; mimeType: string };
}> {
  const uploadResult = await onUploadFile({
    file: selectedFile,
    contentType: selectedFileType,
  });
  return {
    uploads: [uploadResult.upload],
    fileUrl: uploadResult.fileUrl,
    fileMetadata: uploadResult.fileMetadata,
  };
}

export function ChatWindow({
  bot,
  messages,
  onSendMessage,
  onNewConversation,
  onBack,
  isSending,
  isLoadingMessages,
  isLoadingMoreMessages,
  hasMoreMessages,
  onLoadMoreMessages,
  conversationMode,
  inputConfig,
  onUploadFile,
  conversations,
  onSelectConversation,
  onHideConversation,
  currentConversationId,
  limitState,
  onClearLimit,
  failedMessageContent: externalFailedMessage,
  onFailedMessageCleared,
  stickyLabel = null,
  hideBack = false,
}: ChatWindowProps) {
  const [inputValue, setInputValue] = useState('');

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<AttachmentContentType | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [pendingFileType, setPendingFileType] = useState<AttachmentContentType | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRecordingLocked, setIsRecordingLocked] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openConversationMenuId, setOpenConversationMenuId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const recordingStartYRef = useRef<number | null>(null);
  const recordingLockedRef = useRef(false);

  const limitReached = limitState?.reached ?? false;
  const hasStreamingPlaceholder = messages.some(message => message.id.startsWith('streaming-'));
  const timeLeft = useLimitCountdown(limitState?.resetAt, onClearLimit);
  const menuCards = useMemo(() => {
    if (bot.kind !== 'CONCIERGE') {
      return [];
    }
    const lastBot = [...messages]
      .reverse()
      .find(message => message.sender === 'BOT' && message.contentType === 'TEXT');
    return lastBot ? parseConciergeMenuCards(lastBot.content) : [];
  }, [bot.kind, messages]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setOpenConversationMenuId(null);
  }, [currentConversationId]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  // Handle mobile keyboard viewport issue (iOS Safari)
  useEffect(() => {
    const applyVisualViewport = () => {
      const viewport = window.visualViewport;
      if (!viewport) {
        return;
      }
      document.documentElement.style.setProperty('--viewport-height', `${Math.round(viewport.height)}px`);
      document.documentElement.style.setProperty('--viewport-offset-top', `${Math.round(viewport.offsetTop)}px`);
    };

    applyVisualViewport();
    window.visualViewport?.addEventListener('resize', applyVisualViewport);
    window.visualViewport?.addEventListener('scroll', applyVisualViewport);
    return () => {
      window.visualViewport?.removeEventListener('resize', applyVisualViewport);
      window.visualViewport?.removeEventListener('scroll', applyVisualViewport);
    };
  }, []);

  // Repoblar input cuando el bot falla y se recibe un mensaje fallido externo
  useEffect(() => {
    if (externalFailedMessage && externalFailedMessage.trim()) {
      setInputValue(externalFailedMessage);
      onFailedMessageCleared?.();
      // Focus the textarea para que el usuario pueda continuar escribiendo
      textareaRef.current?.focus();
    }
  }, [externalFailedMessage, onFailedMessageCleared]);

  const clearSelectedFile = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setSelectedFileType(null);
    setPreviewUrl(null);
    setFileError(null);
  }, [previewUrl]);

  const validateFile = useCallback(
    (file: File, contentType: AttachmentContentType): boolean => {
      const validationError = validateAttachmentAgainstConfig(file, contentType, inputConfig);
      setFileError(validationError);
      return !validationError;
    },
    [inputConfig]
  );

  const applySelectedFile = useCallback(
    (file: File, contentType: AttachmentContentType) => {
      const normalizedFile =
        contentType === 'audio' ? normalizeAudioFileMimeByExtension(file) : file;

      if (!validateFile(normalizedFile, contentType)) return;

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setSelectedFile(normalizedFile);
      setSelectedFileType(contentType);

      if (contentType === 'image' || contentType === 'audio') {
        setPreviewUrl(URL.createObjectURL(normalizedFile));
      } else {
        setPreviewUrl(null);
      }
    },
    [previewUrl, validateFile]
  );

  const sendAttachmentMessage = useCallback(
    async (file: File, contentType: AttachmentContentType, content = '') => {
      if (isSending || isUploading || limitReached) return;

      setIsUploading(true);
      try {
        const uploadResult = await uploadSelectedAttachment(file, contentType, onUploadFile);

        await onSendMessage({
          content,
          contentType: resolveOutgoingContentType(contentType),
          uploads: uploadResult.uploads,
          fileUrl: uploadResult.fileUrl,
          fileMetadata: uploadResult.fileMetadata,
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        setFileError(error instanceof Error ? error.message : 'No se pudo subir el archivo');
      } finally {
        setIsUploading(false);
      }
    },
    [isSending, isUploading, limitReached, onSendMessage, onUploadFile]
  );

  const sendRecordedAudio = useCallback(
    async (file: File) => {
      const normalizedFile = normalizeAudioFileMimeByExtension(file);
      if (!validateFile(normalizedFile, 'audio')) return;

      await sendAttachmentMessage(normalizedFile, 'audio');
    },
    [sendAttachmentMessage, validateFile]
  );

  const { isRecording, recordingSeconds, cancelRecording, startRecording, stopRecording } =
    useAudioRecording({
      onRecordingComplete: sendRecordedAudio,
      audioEnabled: inputConfig?.audio?.enabled,
      maxDurationSeconds: inputConfig?.audio?.maxDurationSeconds,
      setFileError,
    });

  const setRecordingLockState = useCallback((locked: boolean) => {
    recordingLockedRef.current = locked;
    setIsRecordingLocked(locked);
  }, []);

  const finishRecording = useCallback(() => {
    setRecordingLockState(false);
    recordingStartYRef.current = null;
    stopRecording();
  }, [setRecordingLockState, stopRecording]);

  const discardRecording = useCallback(() => {
    setRecordingLockState(false);
    recordingStartYRef.current = null;
    cancelRecording();
  }, [cancelRecording, setRecordingLockState]);

  useEffect(() => {
    if (!isRecording) return;
    // Una vez fija la grabación (manos libres), se controla solo con los
    // botones Enviar/Cancelar. No registramos listeners globales para que el
    // scroll o un toque accidental nunca interrumpan ni corten el audio.
    if (isRecordingLocked) return;

    const sendOnRelease = () => {
      if (!recordingLockedRef.current) {
        finishRecording();
      }
    };
    // Un gesto interrumpido (el navegador reinterpreta el toque como scroll, o
    // el SO interrumpe el puntero) NO debe descartar el audio. Lo fijamos para
    // que la grabación continúe y el usuario decida enviar o cancelar.
    const preserveOnInterruptedGesture = () => {
      setRecordingLockState(true);
    };
    const lockIfDraggedUp = (clientY: number) => {
      const startY = recordingStartYRef.current;
      if (startY === null || recordingLockedRef.current) return;
      if (startY - clientY >= 56) {
        setRecordingLockState(true);
      }
    };
    const handlePointerMove = (event: PointerEvent) => {
      lockIfDraggedUp(event.clientY);
    };
    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        lockIfDraggedUp(touch.clientY);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', sendOnRelease);
    window.addEventListener('mouseup', sendOnRelease);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', sendOnRelease);
    window.addEventListener('pointercancel', preserveOnInterruptedGesture);
    window.addEventListener('touchcancel', preserveOnInterruptedGesture);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', sendOnRelease);
      window.removeEventListener('mouseup', sendOnRelease);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', sendOnRelease);
      window.removeEventListener('pointercancel', preserveOnInterruptedGesture);
      window.removeEventListener('touchcancel', preserveOnInterruptedGesture);
    };
  }, [finishRecording, isRecording, isRecordingLocked, setRecordingLockState]);

  const buildFileAccept = (): string => {
    const allowed = inputConfig?.files?.enabled ? inputConfig.files.allowedExtensions || [] : [];
    const sourceExtensions = allowed.length > 0 ? allowed : DEFAULT_FILE_ACCEPT_EXTENSIONS;
    const normalizedAllowed = sourceExtensions.map(value =>
      value.startsWith('.') ? value : `.${value}`
    );
    return normalizedAllowed.join(',');
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      setPendingFileType('file');
      fileInputRef.current.accept = buildFileAccept();
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.click();
    }
  };

  const triggerCameraCapture = () => {
    if (cameraInputRef.current) {
      setPendingFileType('image');
      cameraInputRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !pendingFileType) return;
    const isGenericAttachment = pendingFileType === 'file';
    let resolvedFileType = pendingFileType;
    if (isGenericAttachment && inputConfig?.audio?.enabled && isAudioFile(file)) {
      resolvedFileType = 'audio';
    } else if (isGenericAttachment && inputConfig?.images?.enabled !== false && isImageFile(file)) {
      resolvedFileType = 'image';
    }
    applySelectedFile(file, resolvedFileType);
    e.target.value = '';
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSend = useCallback(async () => {
    const trimmedContent = inputValue.trim();
    const hasAttachment = !!selectedFile && !!selectedFileType;

    if ((!trimmedContent && !hasAttachment) || isSending || isUploading || limitReached) return;

    let uploads: FlowiseUpload[] | undefined;
    let fileUrl: string | undefined;
    let fileMetadata: { name: string; size: number; mimeType: string } | undefined;

    if (hasAttachment) {
      setIsUploading(true);
      try {
        const uploadResult = await uploadSelectedAttachment(
          selectedFile,
          selectedFileType,
          onUploadFile
        );
        uploads = uploadResult.uploads;
        fileUrl = uploadResult.fileUrl;
        fileMetadata = uploadResult.fileMetadata;
      } catch (error) {
        console.error('Error uploading file:', error);
        setIsUploading(false);
        setFileError(error instanceof Error ? error.message : 'No se pudo subir el archivo');
        return;
      }
      setIsUploading(false);
    }

    const content = getAttachmentMessageContent(trimmedContent, selectedFile, selectedFileType);
    const contentType: ChatbotContentType = hasAttachment
      ? resolveOutgoingContentType(selectedFileType)
      : 'TEXT';

    setInputValue('');
    clearSelectedFile();

    await onSendMessage({
      content,
      contentType,
      uploads,
      fileUrl,
      fileMetadata,
    });
  }, [
    inputValue,
    selectedFile,
    selectedFileType,
    isSending,
    isUploading,
    limitReached,
    onSendMessage,
    onUploadFile,
    clearSelectedFile,
  ]);

  const debouncedSend = useDebouncedCallback(() => {
    void handleSend();
  }, 250);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        debouncedSend();
      }
    },
    [debouncedSend]
  );

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  let limitLabel = 'total';
  if (limitState?.reason === 'hourly') {
    limitLabel = 'por hora';
  } else if (limitState?.reason === 'daily') {
    limitLabel = 'por día';
  }

  const maxRecordingSeconds = inputConfig?.audio?.maxDurationSeconds ?? 120;
  const canRecordAudio =
    typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
  const microphoneUnavailableMessage =
    typeof window !== 'undefined' && window.isSecureContext === false
      ? 'Para grabar audio desde el microfono, abri el chat con HTTPS. En HTTP el navegador bloquea el microfono.'
      : 'Este navegador no permite grabar audio desde el micrófono.';
  const hasSendableContent = !!inputValue.trim() || !!selectedFile;
  let audioActionLabel = 'Mantener presionado para grabar audio';
  if (isRecordingLocked) {
    audioActionLabel = 'Grabación bloqueada';
  } else if (isRecording) {
    audioActionLabel = 'Soltar para enviar audio';
  }

  const handleAudioPressStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    if (!canRecordAudio) {
      setFileError(microphoneUnavailableMessage);
      return;
    }
    if (isSending || isUploading || limitReached || isRecording) return;

    recordingStartYRef.current = event.clientY;
    setRecordingLockState(false);
    startRecording();
  };

  const handleAudioPressEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!recordingLockedRef.current) {
      finishRecording();
    }
  };

  const handleAudioPressCancel = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    // El puntero sobre el botón se canceló (p. ej. el gesto pasó a scroll).
    // No descartamos: fijamos la grabación para no perder el audio.
    if (isRecording) {
      setRecordingLockState(true);
    }
  };

  const handleAudioKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (!canRecordAudio) {
      setFileError(microphoneUnavailableMessage);
      return;
    }
    if (!isRecording && !isSending && !isUploading && !limitReached) {
      recordingStartYRef.current = null;
      setRecordingLockState(false);
      startRecording();
    }
  };

  const handleAudioKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (isRecording && !recordingLockedRef.current) {
      finishRecording();
    }
  };

  const renderMessagesContent = () => {
    if (isLoadingMessages) {
      return (
        <div className='space-y-6'>
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className={`flex animate-pulse ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`h-16 w-64 animate-shimmer rounded-2xl ${
                  i % 2 === 0
                    ? 'bg-gradient-to-r from-violet-200 via-violet-100 to-violet-200 dark:from-violet-900/50 dark:via-violet-800/50 dark:to-violet-900/50'
                    : 'bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-800/50 dark:via-slate-700/50 dark:to-slate-800/50'
                } bg-[length:200%_100%]`}
              />
            </div>
          ))}
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className='flex h-full flex-col items-center justify-center p-8'>
          <div className='animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-700 max-w-2xl w-full text-center'>
            <div className='relative mx-auto mb-8 h-24 w-24'>
              <div className='absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-tr from-primary to-primary-700 blur-2xl opacity-40' />
              <div className='relative flex h-full w-full items-center justify-center rounded-3xl bg-gradient-to-br from-white to-slate-100 shadow-2xl shadow-violet-500/20 dark:from-slate-800 dark:to-slate-900 border border-white/50 dark:border-slate-700/50'>
                {bot.iconUrl ? (
                  <img
                    src={bot.iconUrl}
                    alt={bot.name}
                    className='h-full w-full rounded-3xl object-cover'
                  />
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='h-10 w-10 text-violet-500'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z'
                    />
                  </svg>
                )}
              </div>
              <div className='absolute -bottom-2 -right-2 h-6 w-6 rounded-full border-4 border-slate-50 bg-emerald-500 dark:border-slate-800' />
            </div>

            <h3 className='mb-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-white'>
              Hola, soy{' '}
              <span className='text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary-700'>
                {bot.name}
              </span>
            </h3>
            <p className='mx-auto max-w-md text-lg text-slate-500 dark:text-slate-400 mb-10 leading-relaxed'>
              {bot.description ||
                'Estoy aquí para ayudarte con lo que necesites. Pregúntame lo que quieras.'}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className='space-y-6'>
        {hasMoreMessages && (
          <div className='flex justify-center'>
            <Button
              variant='outline'
              size='sm'
              onClick={onLoadMoreMessages}
              disabled={isLoadingMoreMessages}
            >
              {isLoadingMoreMessages ? 'Cargando...' : 'Cargar más'}
            </Button>
          </div>
        )}
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            isStreaming={message.id.startsWith('streaming-')}
            index={index}
          />
        ))}
        {isSending && !hasStreamingPlaceholder && (
          <MessageBubble
            message={{
              id: 'typing',
              sender: 'BOT',
              contentType: 'TEXT',
              content: '',
              fileUrl: null,
              createdAt: new Date().toISOString(),
            }}
            isStreaming
          />
        )}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  return (
    <div
      data-testid='chat-window'
      className='flex h-full overflow-hidden bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background'
    >
      {/* Animated background blobs */}
      <div className='pointer-events-none fixed inset-0 overflow-hidden'>
        <div className='absolute -top-40 -right-40 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
        <div className='absolute -bottom-40 -left-40 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1s]' />
      </div>
      {/* Conversation Sidebar (Desktop) */}
      {conversationMode === 'MULTIPLE' && (
        <div
          className={`relative z-20 flex flex-col border-r border-slate-200/80 bg-white/80 backdrop-blur-xl transition-all duration-300 dark:border-slate-700/80 dark:bg-slate-900/80 overflow-hidden
          ${showConversations ? 'translate-x-0 w-80' : '-translate-x-full w-0 opacity-0'}
          md:translate-x-0 ${sidebarCollapsed ? 'md:w-0 md:opacity-0 md:border-r-0' : 'md:w-80 md:opacity-100'}`}
        >
          <div className='flex items-center justify-between p-4 border-b border-slate-200/50 dark:border-slate-700/50'>
            <h2 className='font-semibold text-slate-800 dark:text-slate-200'>Conversaciones</h2>
            {onNewConversation && (
              <Button variant='ghost' size='icon' onClick={onNewConversation} className='h-8 w-8'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='w-5 h-5'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' d='M12 4.5v15m7.5-7.5h-15' />
                </svg>
              </Button>
            )}
          </div>
          <div className='flex-1 overflow-y-auto p-2 space-y-1'>
            {conversations.map(conv => (
              <div key={conv.id} className='relative'>
                <button
                  type='button'
                  onClick={() => onSelectConversation(conv.id)}
                  className={`w-full text-left p-3 pr-10 rounded-xl transition-colors ${
                    currentConversationId === conv.id
                      ? 'bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-100'
                      : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  <div className='flex items-center justify-between gap-2'>
                    <div className='font-medium truncate'>
                      {conv.lastMessagePreview || 'Nueva conversación'}
                    </div>
                    <span className='text-xs text-slate-500'>{conv.messageCount} msg</span>
                  </div>
                  <div className='text-xs opacity-70 mt-1'>
                    Inicio: {new Date(conv.startedAt).toLocaleDateString()} · Último:{' '}
                    {new Date(conv.lastMessageAt).toLocaleDateString()}
                  </div>
                </button>

                {onHideConversation && (
                  <>
                    <button
                      type='button'
                      aria-label='Opciones de conversación'
                      onClick={event => {
                        event.stopPropagation();
                        setOpenConversationMenuId(openConversationMenuId === conv.id ? null : conv.id);
                      }}
                      className='absolute right-2 top-2 rounded-lg p-1.5 text-slate-500 transition-colors hover:bg-white/80 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        viewBox='0 0 24 24'
                        fill='currentColor'
                        className='h-4 w-4'
                      >
                        <path d='M12 8a2 2 0 100-4 2 2 0 000 4zM12 14a2 2 0 100-4 2 2 0 000 4zM12 20a2 2 0 100-4 2 2 0 000 4z' />
                      </svg>
                    </button>

                    {openConversationMenuId === conv.id && (
                      <div className='absolute right-2 top-9 z-30 min-w-32 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900'>
                        <button
                          type='button'
                          onClick={event => {
                            event.stopPropagation();
                            setOpenConversationMenuId(null);
                            onHideConversation(conv.id);
                          }}
                          className='w-full rounded-md px-3 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className='relative z-10 flex h-full min-w-0 flex-1 flex-col'>
        {/* Header with glassmorphism - clearer border */}
        <header className='relative z-10 flex min-w-0 items-center justify-between border-b border-slate-200/80 bg-white/80 px-3 py-3 shadow-sm backdrop-blur-xl dark:border-slate-700/80 dark:bg-slate-900/80 sm:px-4'>
          <div className='flex min-w-0 items-center gap-2 sm:gap-3'>
            <button
              onClick={onBack}
              className={`shrink-0 rounded-xl p-2 text-slate-600 transition-all hover:bg-slate-100 active:scale-95 dark:text-slate-400 dark:hover:bg-slate-800 ${hideBack ? 'invisible pointer-events-none' : ''}`}
              aria-label='Volver'
              disabled={hideBack}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={2}
                stroke='currentColor'
                className='h-5 w-5'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M15.75 19.5L8.25 12l7.5-7.5'
                />
              </svg>
            </button>

            {/* Mobile toggle sidebar */}
            {conversationMode === 'MULTIPLE' && (
              <button
                onClick={() => setShowConversations(!showConversations)}
                className='md:hidden rounded-xl p-2 text-slate-600 transition-all hover:bg-slate-100 active:scale-95 dark:text-slate-400 dark:hover:bg-slate-800'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='w-5 h-5'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
                  />
                </svg>
              </button>
            )}

            {/* Desktop toggle sidebar */}
            {conversationMode === 'MULTIPLE' && (
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className='hidden md:flex rounded-xl p-2 text-slate-600 transition-all hover:bg-slate-100 active:scale-95 dark:text-slate-400 dark:hover:bg-slate-800'
                aria-label={sidebarCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
              >
                {sidebarCollapsed ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='w-5 h-5'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='w-5 h-5'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5'
                    />
                  </svg>
                )}
              </button>
            )}

            {/* Bot avatar with glow effect */}
            <div className='relative shrink-0 self-center'>
              <div className='absolute inset-0 animate-pulse-slow rounded-full bg-gradient-to-r from-primary to-primary-700 blur-md opacity-50' />
              {bot.iconUrl ? (
                <img
                  src={bot.iconUrl}
                  alt={bot.name}
                  className='relative h-10 w-10 rounded-full border-2 border-white object-cover shadow-lg dark:border-slate-700 sm:h-11 sm:w-11'
                />
              ) : (
                <div className='relative flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-primary to-primary-700 shadow-lg dark:border-slate-700 sm:h-11 sm:w-11'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='ml-1 h-5 w-5 text-white'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z'
                    />
                  </svg>
                </div>
              )}
              <span className='absolute bottom-0 right-0 flex h-3.5 w-3.5'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75' />
                <span className='relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-white dark:border-slate-900 bg-emerald-500' />
              </span>
            </div>

            <div className='min-w-0'>
              <h1 className='truncate text-lg font-semibold leading-tight text-slate-900 dark:text-white sm:text-base'>
                {bot.name}
              </h1>
              {stickyLabel ? (
                <div className='mt-1 flex flex-wrap items-center gap-2'>
                  <p className='text-xs font-medium text-slate-600 dark:text-slate-300'>
                    Te atiende {stickyLabel}
                  </p>
                  <button
                    type='button'
                    className='rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700 hover:bg-violet-200'
                    onClick={() => {
                      void onSendMessage({ content: 'menú', contentType: 'TEXT' });
                    }}
                    disabled={isSending || limitReached}
                  >
                    Menú
                  </button>
                </div>
              ) : (
                <p className='flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400'>
                  <span className='relative flex h-2 w-2'>
                    <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75' />
                    <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
                  </span>
                  En línea
                </p>
              )}
            </div>
          </div>

          {conversationMode === 'MULTIPLE' && onNewConversation && (
            <div className='hidden md:block'>
              {/* Desktop new conversation button if needed, but sidebar has it */}
            </div>
          )}
        </header>

        {/* Messages area */}
        <div
          ref={messagesContainerRef}
          className='relative z-10 flex-1 overflow-y-auto px-4 py-6 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600'
        >
          {renderMessagesContent()}
        </div>

        {/* Input Area - Fixed Bottom */}
        <div className='z-20 border-t border-slate-200/60 bg-white/70 px-2.5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl dark:border-slate-800/60 dark:bg-slate-950/70 sm:px-4 sm:pb-4 sm:pt-4 md:p-6'>
          <div className='mx-auto w-full max-w-3xl'>
            {limitReached && (
              <div className='mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-700'>
                <div className='font-medium'>Límite {limitLabel} alcanzado</div>
                {limitState?.resetAt ? (
                  <div>Podrás enviar nuevamente en {formatCountdown(timeLeft)}.</div>
                ) : (
                  <div>No puedes enviar más mensajes en esta conversación.</div>
                )}
              </div>
            )}

            {isRecording && (
              <div
                className={`mb-3 flex items-center justify-between gap-3 rounded-full border px-4 py-2 text-xs shadow-sm ${
                  isRecordingLocked
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <span
                    className={`h-2.5 w-2.5 animate-pulse rounded-full ${
                      isRecordingLocked ? 'bg-emerald-500' : 'bg-red-500'
                    }`}
                  />
                  <span className='font-medium'>{formatCountdown(recordingSeconds)}</span>
                  {isRecordingLocked ? (
                    <>
                      <span className='font-medium text-emerald-600'>Grabación bloqueada</span>
                      <span className='hidden text-emerald-500 sm:inline'>
                        · tocá enviar o cancelar
                      </span>
                    </>
                  ) : (
                    <>
                      <span className='text-red-500'>Soltá para enviar</span>
                      <span className='hidden text-red-400 sm:inline'>
                        · deslizá hacia arriba para fijar
                      </span>
                    </>
                  )}
                </div>
                <div className='flex items-center gap-2'>
                  {isRecordingLocked && (
                    <button
                      type='button'
                      onClick={finishRecording}
                      className='rounded-full bg-emerald-600 px-3 py-1 font-medium text-white hover:bg-emerald-700'
                    >
                      Enviar audio
                    </button>
                  )}
                  <button
                    type='button'
                    onClick={discardRecording}
                    className={`rounded-full px-2 py-1 font-medium ${
                      isRecordingLocked
                        ? 'text-emerald-700 hover:bg-emerald-100'
                        : 'text-red-600 hover:bg-red-100'
                    }`}
                  >
                    Cancelar
                  </button>
                </div>
                <span className='sr-only'>
                  Duración máxima: {formatCountdown(maxRecordingSeconds)}
                </span>
              </div>
            )}

            {fileError && (
              <div className='mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700'>
                {fileError}
              </div>
            )}

            {/* File Preview */}
            {selectedFile && (
              <div className='mb-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800'>
                <div className='flex items-start gap-3'>
                  <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100 text-primary dark:bg-violet-900/30 dark:text-violet-400'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                      stroke='currentColor'
                      className='w-6 h-6'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 overflow-hidden'>
                    <p className='truncate text-sm font-medium text-slate-700 dark:text-slate-200'>
                      {selectedFile.name}
                    </p>
                    <p className='text-xs text-slate-500'>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>

                    {previewUrl && selectedFileType === 'image' && (
                      <img
                        src={previewUrl}
                        alt='Vista previa'
                        className='mt-2 max-h-40 w-full rounded-md object-cover'
                      />
                    )}

                    {previewUrl && selectedFileType === 'audio' && (
                      <audio
                        controls
                        preload='metadata'
                        className='mt-2 w-full'
                        src={previewUrl}
                        aria-label='Previsualización de audio'
                      />
                    )}
                  </div>
                  <button
                    onClick={clearSelectedFile}
                    className='rounded-full p-1 hover:bg-slate-200 dark:hover:bg-slate-700'
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.5}
                      stroke='currentColor'
                      className='w-5 h-5 text-slate-500'
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {menuCards.length > 0 && (
              <div className='mb-2 flex flex-wrap gap-2' data-testid='concierge-menu-cards'>
                {menuCards.map(card => (
                  <button
                    key={card.index}
                    type='button'
                    disabled={isSending || limitReached}
                    className='rounded-full border border-violet-200 bg-white px-3 py-1.5 text-sm font-medium text-violet-700 shadow-sm hover:border-violet-400 dark:border-violet-800 dark:bg-slate-800 dark:text-violet-200'
                    onClick={() => {
                      void onSendMessage({ content: String(card.index), contentType: 'TEXT' });
                    }}
                  >
                    {card.index}. {card.label}
                  </button>
                ))}
              </div>
            )}
            <div className='flex w-full min-w-0 items-end gap-1.5 sm:gap-3'>
              {/* Message capsule */}
              <div className='flex min-w-0 flex-1 items-end gap-0.5 rounded-[1.75rem] border border-slate-200 bg-white px-2 py-1.5 shadow-sm transition-all duration-200 focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-500/15 dark:border-slate-700 dark:bg-slate-800 sm:gap-1 sm:px-3'>
                <input
                  type='file'
                  ref={fileInputRef}
                  className='hidden'
                  onChange={handleFileSelect}
                />
                <input
                  type='file'
                  ref={cameraInputRef}
                  className='hidden'
                  accept='image/*'
                  capture='environment'
                  onChange={handleFileSelect}
                />

                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder='Mensaje'
                  disabled={isSending || isUploading || limitReached}
                  rows={1}
                  className='min-h-[38px] max-h-[96px] min-w-0 flex-1 resize-none border-0 bg-transparent px-1 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-slate-500 sm:max-h-[120px] sm:text-sm'
                />

                <div className='flex shrink-0 items-center gap-0.5 pb-0.5'>
                  {inputConfig?.files?.enabled && (
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 text-slate-500 hover:bg-slate-100 hover:text-emerald-600 dark:text-slate-400 dark:hover:bg-slate-700 sm:h-9 sm:w-9'
                      onClick={triggerFileSelect}
                      aria-label='Adjuntar archivo'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.8}
                        stroke='currentColor'
                        className='h-5 w-5'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13'
                        />
                      </svg>
                    </Button>
                  )}
                  {inputConfig?.images?.enabled !== false && (
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 text-slate-500 hover:bg-slate-100 hover:text-emerald-600 dark:text-slate-400 dark:hover:bg-slate-700 sm:h-9 sm:w-9'
                      onClick={triggerCameraCapture}
                      aria-label='Sacar foto'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                        className='h-5 w-5'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M6.827 6.175A2.31 2.31 0 019.186 4.5h5.628a2.31 2.31 0 012.359 1.675l.513 1.927A1.125 1.125 0 0018.772 9H20.25A2.25 2.25 0 0122.5 11.25v6A2.25 2.25 0 0120.25 19.5H3.75A2.25 2.25 0 011.5 17.25v-6A2.25 2.25 0 013.75 9h1.478c.51 0 .956-.343 1.086-.838l.513-1.987z'
                        />
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M15.75 14.25a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z'
                        />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>

              {hasSendableContent || !inputConfig?.audio?.enabled ? (
                <Button
                  onClick={debouncedSend}
                  disabled={!hasSendableContent || isSending || isUploading || limitReached}
                  size='icon'
                  aria-label='Enviar mensaje'
                  className={`h-11 w-11 shrink-0 rounded-full transition-all duration-200 sm:h-14 sm:w-14 ${
                    hasSendableContent && !isSending && !isUploading && !limitReached
                      ? 'bg-emerald-500 text-white shadow-lg hover:scale-105 hover:bg-emerald-600 active:scale-95'
                      : 'bg-slate-200 text-slate-400 dark:bg-slate-700'
                  }`}
                >
                  {isSending || isUploading ? (
                    <Spinner className='h-5 w-5' />
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={2.5}
                      stroke='currentColor'
                      className='h-5 w-5'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5'
                      />
                    </svg>
                  )}
                </Button>
              ) : (
                <Button
                  variant='ghost'
                  size='icon'
                  title={audioActionLabel}
                  className={`h-11 w-11 shrink-0 select-none touch-none rounded-full transition-all duration-200 sm:h-14 sm:w-14 ${
                    isRecording
                      ? isRecordingLocked
                        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                        : 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                      : 'bg-emerald-500 text-white shadow-lg hover:scale-105 hover:bg-emerald-600 active:scale-95'
                  }`}
                  onPointerDown={handleAudioPressStart}
                  onPointerUp={handleAudioPressEnd}
                  onPointerCancel={handleAudioPressCancel}
                  onKeyDown={handleAudioKeyDown}
                  onKeyUp={handleAudioKeyUp}
                  aria-label={audioActionLabel}
                >
                  {isRecording ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='currentColor'
                      viewBox='0 0 24 24'
                      className='h-4 w-4'
                    >
                      <rect x='6' y='6' width='12' height='12' rx='2' />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth={1.8}
                      stroke='currentColor'
                      className='h-6 w-6'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'
                      />
                    </svg>
                  )}
                </Button>
              )}
            </div>
            <p className='mt-2.5 hidden text-center text-xs text-slate-400 sm:block'>
              Enter para enviar · Shift + Enter para nueva línea
            </p>
          </div>
        </div>
      </div>{' '}
      {/* End of flex-1 */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite linear;
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
